import './tailwind.css';
import './i18n';
import React from 'react';
import ReactDOM from 'react-dom/client';
import type { PageProps } from 'gatsby';
import type { WindowLocation } from '@reach/router';
import { WrapRoot } from './src/components/WrapRoot';
import { WrapPage } from './src/components/WrapPage';
import { version } from './package.json';
import { initIframeActivityTracking, sendToParent } from './src/utils/embed/postMessageBridge';
import { initObservability } from './src/services/observability';
import { getBlogMetaBySlug } from './src/utils/blog';

type WrapRootElementArgs = { element: React.ReactElement; };
type WrapPageElementArgs = { element: React.ReactElement; props: PageProps; };
type ReplaceHydrateFunction = () => (element: React.ReactNode, container: Element) => void;
type OnRouteUpdateArgs = { location: WindowLocation; prevLocation?: WindowLocation | null; };

const TITLE_SUFFIX = 'MetaDefender Cloud';
const DEFAULT_TITLE = TITLE_SUFFIX;

const withTitleSuffix = (title: string): string => {
    return title.includes('MetaDefender') ? title : `${title} | ${TITLE_SUFFIX}`;
};

/** Strip trailing slashes in O(n) without `/+$/` (avoids super-linear regex risk on pathological input). */
const trimTrailingSlashes = (path: string): string => {
    let end = path.length;
    while (end > 0 && path[end - 1] === '/') {
        end--;
    }
    return path.slice(0, end);
};

const formatSegmentLabel = (segment: string): string => {
    return segment.replaceAll(/[-_]+/g, ' ').replaceAll(/\b\w/g, (character) => character.toUpperCase());
};

const getRouteTitle = (pathname: string): string => {
    if (pathname === '/' || pathname.startsWith('/dashboard/')) {
        return withTitleSuffix('Dashboard');
    }

    if (pathname === '/analyze') {
        return withTitleSuffix('Analyze');
    }

    if (pathname === '/account/api-key') {
        return withTitleSuffix('API Key');
    }

    if (pathname === '/reports') {
        return withTitleSuffix('Reports');
    }

    if (pathname === '/cswg' || pathname.startsWith('/cswg/')) {
        return withTitleSuffix('CSWG Proxy');
    }

    if (pathname === '/licensing') {
        return withTitleSuffix('Licensing');
    }

    if (pathname === '/extension/get-apikey') {
        return withTitleSuffix('Browser Extension');
    }

    const settingsMatch = pathname.match(/^\/settings\/([^/]+)\/?$/);
    if (settingsMatch) {
        return withTitleSuffix(`${formatSegmentLabel(settingsMatch[1])} Settings`);
    }

    if (pathname.startsWith('/secure-pipeline-studio')) {
        return withTitleSuffix('Secure Pipeline Studio');
    }

    const blogSegments = trimTrailingSlashes(pathname).split('/').filter(Boolean);
    if (blogSegments[0] === 'blog') {
        const meta = blogSegments.length >= 2 ? getBlogMetaBySlug(blogSegments[1]) : undefined;
        return withTitleSuffix(meta?.seoTitle ?? meta?.titleEn ?? 'Blog');
    }

    const pathSegments = trimTrailingSlashes(pathname).split('/').filter(Boolean);
    const isResultsRoute = pathSegments[0] === 'results' && pathSegments.length >= 3;
    if (isResultsRoute) {
        const typeTitleMap: Record<string, string> = {
            file: 'File Analysis Results',
            hash: 'Hash Lookup Results',
            ip: 'IP Lookup Results',
            domain: 'Domain Lookup Results',
            url: 'URL Lookup Results',
        };

        return withTitleSuffix(typeTitleMap[pathSegments[1]] ?? 'Scan Results');
    }

    if (pathname === '/404' || pathname === '/404.html') {
        return withTitleSuffix('Page Not Found');
    }

    return DEFAULT_TITLE;
};

const applyFallbackTitle = (pathname: string): void => {
    if (typeof document === 'undefined') {
        return;
    }

    const seoTitle = document.querySelector('title[data-mdc-seo="1"]');
    if (seoTitle?.textContent && seoTitle.textContent.trim().length > 0) {
        return;
    }

    const nextTitle = getRouteTitle(pathname);
    if (document.title !== nextTitle) {
        document.title = nextTitle;
    }
};

const syncTitleFromPath = (pathname: string): void => {
    if (typeof window === 'undefined') {
        return;
    }

    // Gatsby Head updates can land slightly after onRouteUpdate. We run once
    // on the next frame, then once more shortly after to avoid locking onto a
    // stale title node from the previous page.
    window.requestAnimationFrame(() => applyFallbackTitle(pathname));
    window.setTimeout(() => applyFallbackTitle(pathname), 120);
};

export const wrapRootElement = ({ element }: WrapRootElementArgs): React.ReactElement => {
    return <WrapRoot>{element}</WrapRoot>;
};

export const wrapPageElement = ({ element, props }: WrapPageElementArgs): React.ReactElement => {
    return <WrapPage {...props}>{element}</WrapPage>;
};

export const replaceHydrateFunction: ReplaceHydrateFunction = () => {
    return (element: React.ReactNode, container: Element) => {
        container.setAttribute('data-version', version);
        const root = ReactDOM.createRoot(container);
        root.render(element);
    };
};

/**
 * Called by Gatsby after every client-side route change (including the first
 * render).  We forward the navigation event to the parent site so it can track
 * which page inside the iframe the user is viewing without needing to access
 * the iframe's DOM or URL directly.
 *
 * Navigation events bypass the "active" gate — the parent always needs to know
 * the current route regardless of focus state.
 *
 * Deduplication: In Gatsby dev mode, HMR can trigger onRouteUpdate multiple
 * times for the same path without an actual navigation occurring. We suppress
 * duplicate consecutive route_change events so the parent doesn't see noise.
 */
let _lastSentPath: string | null = null;

export const onRouteUpdate = ({ location, prevLocation }: OnRouteUpdateArgs): void => {
    const currentPath = location.pathname + location.search;

    // Skip if the path hasn't actually changed (HMR re-renders in dev)
    if (currentPath === _lastSentPath) return;
    _lastSentPath = currentPath;

    sendToParent('navigation', {
        event: 'route_change',
        path: location.pathname,
        search: location.search,
        prevPath: prevLocation?.pathname ?? null,
    });

    syncTitleFromPath(location.pathname);
};

/**
 * Called once when the Gatsby runtime initialises on the client.
 * Registers focus/blur/pointer listeners that tell the bridge whether the
 * iframe is actively being used, so analytics events are only forwarded
 * to the parent when appropriate.
 */
export const onClientEntry = (): void => {
    initObservability(version);
    initIframeActivityTracking();
    if (typeof window !== 'undefined') {
        syncTitleFromPath(window.location.pathname);
    }
};
